// src/lib/keycloak.ts
//
// Singleton Keycloak client wrapper. Initializes keycloak-js on
// first call, subsequent calls reuse the same instance.
//
// Keycloak setup:
//   - realm:   "benchmark-dev"
//   - client:  "benchmark-public" (PKCE, public)
//   - users:   demo / demo123 (bench-staff)
//              admin / admin123 (bench-staff + bench-approvers)
//
// In dev: connect to Keycloak at http://localhost:8080
// In prod: connect to the Keycloak service via ECS (env-driven)

import Keycloak from "keycloak-js";
import { getKeycloakInitOptions, getAccountType, type AccountType } from "@/lib/auth";

const KEYCLOAK_URL =
  (import.meta as unknown as { env: Record<string, string> }).env?.VITE_KEYCLOAK_URL ||
  "http://localhost:8080";
const REALM = "benchmark-dev";
const CLIENT_ID = "benchmark-public";

// Local-dev escape hatch: when VITE_AUTH_STUB=1 AND this is a vite DEV
// build, initKeycloak returns a fully-authenticated fake admin instead
// of touching the network. import.meta.env.DEV is false in production
// builds, so the stub can NEVER authenticate a deployable build (A16).
// Toggle from the Login screen if you want to "sign out": clear
// `bench-auth-stub` from localStorage and reload.
const AUTH_STUB =
  import.meta.env.DEV &&
  (import.meta as unknown as { env: Record<string, string> }).env?.VITE_AUTH_STUB === "1";
const STUB_FLAG = "bench-auth-stub";

// Bearer mode keeps tokens in MEMORY ONLY (M2-BFF / A16): no
// access/refresh tokens are ever persisted to localStorage. A full
// page reload signs the user back to anonymous; the default BFF mode
// keeps continuity through the HttpOnly server-side session instead.

let instance: Keycloak | null = null;
let initPromise: Promise<Keycloak> | null = null;

type StubGroup = "bench-approvers" | "bench-staff" | "bench-customer-studio-norden";

function buildStubInstance(role: StubGroup): Keycloak {
  // We cast to `Keycloak` because we only touch the fields our AuthProvider
  // actually reads (authenticated, token, tokenParsed). The stub is never
  // used to make real network calls.
  const tokenParsed = {
    preferred_username: "admin@benchmark-av.be",
    email: "admin@benchmark-av.be",
    name: "Admin (stub)",
    groups: [role, "bench-staff"],
  };
  const fakeToken = btoa(JSON.stringify({ stub: true, role }));
  const stub = {
    authenticated: true,
    token: fakeToken,
    tokenParsed,
    refreshToken: "stub-refresh",
    idToken: fakeToken,
    realmAccess: { roles: [] },
    resourceAccess: {},
    clientId: CLIENT_ID,
    realm: REALM,
    serverUrl: KEYCLOAK_URL,
    onAuthSuccess: undefined,
    onAuthLogout: undefined,
    onAuthRefreshSuccess: undefined,
    onAuthError: undefined,
    onTokenExpired: undefined,
    onAuthRefreshError: undefined,
    // login/logout are also stubbed so clicks on /login work.
    login: () => {
      // Already authenticated in stub mode — nothing to do.
    },
    logout: () => {
      localStorage.removeItem(STUB_FLAG);
      window.location.assign("/");
    },
    // Other methods (no-ops; the auth context never calls these in stub mode).
    updateToken: () => Promise.resolve(true),
    clearToken: () => undefined,
    loadUserInfo: () => Promise.resolve({}),
    loadUserProfile: () => Promise.resolve({}),
    createAccountUrl: () => "",
    createLogoutUrl: () => "/",
    createLoginUrl: () => "/",
    register: () => undefined,
    accountManagement: () => undefined,
    init: () => Promise.resolve(true),
  } as unknown as Keycloak;
  return stub;
}

/**
 * Initialize Keycloak and return the instance. Subsequent calls
 * return the same instance (re-using the same auth state).
 *
 * @returns The Keycloak instance, authenticated if login succeeded.
 */
export function initKeycloak(): Promise<Keycloak> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    if (AUTH_STUB) {
      // Treat absence of the flag as "logged out" so refresh sticks.
      const enabled = localStorage.getItem(STUB_FLAG) === "1";
      instance = enabled
        ? buildStubInstance("bench-approvers")
        : ({ authenticated: false } as unknown as Keycloak);
      return instance;
    }
    if (!instance) {
      instance = new Keycloak(getKeycloakInitOptions(KEYCLOAK_URL, REALM, CLIENT_ID));
    }
    try {
      await instance.init({
        // No onLoad "check-sso" — the silent SSO would error on a
        // fresh browser and bounce the user to a Keycloak error
        // page. Instead, /login triggers keycloak.login() explicitly
        // on click. This page starts up anonymous.
        checkLoginIframe: false,
        silentCheckSsoFallback: false,
      });
      // Keep the in-memory token fresh so API calls never carry a dead
      // access token (stale token after idle caused 401s on gated APIs).
      instance.onTokenExpired = () => {
        instance?.updateToken(30).catch(() => undefined);
      };
    } catch (err) {
      console.warn("Keycloak init warning:", err);
    }
    return instance;
  })();
  return initPromise;
}

/**
 * Get the current Keycloak instance (or null if not initialized).
 */
export function getKeycloak(): Keycloak | null {
  return instance;
}

/**
 * Determine the user's account type from the current Keycloak state.
 */
export function currentAccountType(): AccountType {
  if (!instance || !instance.authenticated) return "anonymous";
  const groups = (instance.tokenParsed as { groups?: string[] } | undefined)?.groups ?? [];
  return getAccountType(groups);
}

/**
 * Trigger a Keycloak login flow.
 */
export function login(redirectUri?: string): void {
  if (AUTH_STUB) {
    localStorage.setItem(STUB_FLAG, "1");
    // Force a reload so AuthProvider re-reads the stub state.
    window.location.assign(redirectUri ?? window.location.pathname);
    return;
  }
  if (!instance) return;
  instance.login(redirectUri ? { redirectUri } : undefined);
}

/**
 * Trigger a Keycloak logout flow.
 */
export function logout(redirectUri?: string): void {
  if (AUTH_STUB) {
    localStorage.removeItem(STUB_FLAG);
    window.location.assign(redirectUri ?? "/");
    return;
  }
  if (!instance) return;
  instance.logout(redirectUri ? { redirectUri } : undefined);
}

/**
 * Get the current access token (for API requests).
 */
export function getToken(): string | undefined {
  return instance?.token;
}
